import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService } from '@app/dialog/services';
import { PersonalDetails, PlayerDetails } from '@app/profile/models';
import { ProfileService } from '@app/profile/services';
import {
  FetchPersonalDetails,
  FetchPlayerDetails,
  FetchProfilePicture,
  ResetProfile,
  SetPersonalDetails,
  SetPlayerDetails,
  SetProfilePicture,
} from '@app/profile/store/profile.actions';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, of, tap } from 'rxjs';

export interface ProfileStateModel {
  personalDetails?: PersonalDetails;
  playerDetails?: PlayerDetails;
  profilePicture?: string;
}

const INITIAL_STATE: ProfileStateModel = {
  personalDetails: undefined,
  playerDetails: undefined,
  profilePicture: undefined,
};

@State<ProfileStateModel>({
  name: 'profile',
  defaults: INITIAL_STATE,
})
@Injectable()
export class ProfileState {
  constructor(
    private profileService: ProfileService,
    private notificationService: NotificationService
  ) {}

  @Selector()
  static getPersonalDetails(state: ProfileStateModel): PersonalDetails | undefined {
    return state.personalDetails;
  }

  @Action(SetPersonalDetails)
  setPersonalDetails(ctx: StateContext<ProfileStateModel>, action: SetPersonalDetails): ProfileStateModel {
    return ctx.patchState({ personalDetails: action.personalDetails });
  }

  @Action(FetchPersonalDetails)
  fetchPersonalDetails(
    ctx: StateContext<ProfileStateModel>,
    action: FetchPersonalDetails
  ): Observable<PersonalDetails | undefined> | undefined {
    const { personalDetails } = ctx.getState();

    if (personalDetails && !action.force) {
      return;
    }

    return this.profileService.getPersonalDetails().pipe(
      tap((data) => {
        ctx.patchState({ personalDetails: data });
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.openAsError(
          error.error?.Description || 'PERSONAL_DETAILS.FAILED_TO_FETCH_PERSONAL_DETAILS'
        );
        return of(undefined);
      })
    );
  }

  @Selector()
  static getPlayerDetails(state: ProfileStateModel): PlayerDetails | undefined {
    return state.playerDetails;
  }

  @Action(SetPlayerDetails)
  setPlayerDetails(ctx: StateContext<ProfileStateModel>, action: SetPlayerDetails): ProfileStateModel {
    return ctx.patchState({ playerDetails: action.playerDetails });
  }

  @Action(FetchPlayerDetails)
  fetchPlayerDetails(
    ctx: StateContext<ProfileStateModel>,
    action: FetchPlayerDetails
  ): Observable<PlayerDetails | undefined> | undefined {
    const { playerDetails } = ctx.getState();

    if (playerDetails && !action.force) {
      return;
    }

    return this.profileService.getPlayerDetails().pipe(
      tap((data) => {
        ctx.patchState({ playerDetails: data });
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.openAsError(
          error.error?.Description || 'PLAYER_DETAILS.FAILED_TO_FETCH_PLAYER_DETAILS'
        );
        return of(undefined);
      })
    );
  }

  @Selector()
  static getProfilePicture(state: ProfileStateModel): string | undefined {
    return state.profilePicture;
  }

  @Action(SetProfilePicture)
  setProfilePicture(ctx: StateContext<ProfileStateModel>, action: SetProfilePicture): ProfileStateModel {
    return ctx.patchState({ profilePicture: action.profilePicture });
  }

  @Action(FetchProfilePicture)
  fetchProfilePicture(
    ctx: StateContext<ProfileStateModel>,
    action: FetchProfilePicture
  ): Observable<string | undefined> | undefined {
    const { profilePicture } = ctx.getState();

    if (profilePicture && !action.force) {
      return;
    }

    return this.profileService.getProfilePicture().pipe(
      tap((profilePicture) => {
        ctx.patchState({ profilePicture });
      }),
      catchError(() => {
        return of(undefined);
      })
    );
  }

  @Action(ResetProfile)
  reset(ctx: StateContext<ProfileStateModel>): ProfileStateModel {
    return ctx.patchState({ ...INITIAL_STATE });
  }
}
